import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { motion } from "framer-motion";
import { Lightning } from "@phosphor-icons/react";
import { StatCard, formatNum } from "@/components/insights/InsightsCharts";
import { DriftAnalysisSection } from "@/components/insights/DriftAnalysisSection";
import {
  PlanThisMonthCTA, ContentTypeChart, WeeklyActivityChart,
  BusinessStatusChart, TopTopicsSection, AchievementsSection,
} from "@/components/insights/InsightsDataSections";
import { Lightning as LightningIcon, Notebook, ArrowsClockwise, CalendarBlank } from "@phosphor-icons/react";

const PERIODS = [
  { key: "30d", label: "Last 30 Days" },
  { key: "90d", label: "Last 90 Days" },
  { key: "12m", label: "Last 12 Months" },
];

export default function InsightsPage() {
  const { api, user } = useAuth();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [achievementsOpen, setAchievementsOpen] = useState(false);
  const [driftData, setDriftData] = useState(null);
  const [driftLoading, setDriftLoading] = useState(false);
  const [correctingAssetId, setCorrectingAssetId] = useState("");
  const [driftTrend, setDriftTrend] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/analytics/usage?period=${period}`);
      setData(resp.data);
    } catch (e) { console.error("Failed to load insights:", e); }
    setLoading(false);
  }, [api, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchDrift = useCallback(async () => {
    if (!data?.recent_assets?.length) { setDriftData(null); return; }
    setDriftLoading(true);
    try {
      const resp = await api.post("/analytics/drift-analysis", {
        asset_ids: data.recent_assets.map((a) => a.history_id).filter(Boolean),
      });
      setDriftData(resp.data);
      try {
        const trendResp = await api.get("/analytics/drift-trend?days=90");
        setDriftTrend(trendResp.data?.points || []);
      } catch (e) { console.error("Failed to load drift trend:", e); setDriftTrend(null); }
    } catch (e) { console.error("Failed to load drift data:", e); setDriftData(null); }
    setDriftLoading(false);
  }, [api, data?.recent_assets]);

  useEffect(() => { fetchDrift(); }, [fetchDrift]);

  const proposeCorrection = async (asset) => {
    setCorrectingAssetId(asset.asset_id);
    try {
      await api.post("/analytics/drift-analysis/propose-correction", {
        asset_id: asset.asset_id, flagged_sections: asset.flagged_sections || [],
      });
      await fetchData();
      await fetchDrift();
    } catch (e) { console.error("Failed to propose correction:", e); }
    setCorrectingAssetId("");
  };

  const s = data?.summary || {};
  const isEmpty = (s.total_generated || 0) < 5;

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Page title + period selector */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']" data-testid="insights-title">Insights</h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">Your content creation analytics</p>
          </div>
          <div className="flex gap-1 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-1" data-testid="period-selector">
            {PERIODS.map((p) => (
              <button key={p.key} onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                  period === p.key ? "bg-[#FF4400] text-white shadow-sm" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
                }`}
                data-testid={`period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <div key={`skeleton-${i}`} className="h-28 bg-[var(--bg-card)] rounded-lg animate-pulse border border-[var(--border)]" />)}
            </div>
            <div className="h-64 bg-[var(--bg-card)] rounded-lg animate-pulse border border-[var(--border)]" />
          </div>
        ) : isEmpty ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-20" data-testid="insights-empty">
            <svg viewBox="0 0 120 120" className="w-24 h-24 mx-auto mb-6 text-[var(--text-muted)] opacity-40">
              <rect x="15" y="60" width="16" height="40" rx="3" fill="currentColor" opacity="0.3" />
              <rect x="37" y="40" width="16" height="60" rx="3" fill="currentColor" opacity="0.5" />
              <rect x="59" y="25" width="16" height="75" rx="3" fill="currentColor" opacity="0.7" />
              <rect x="81" y="45" width="16" height="55" rx="3" fill="currentColor" opacity="0.4" />
              <line x1="10" y1="105" x2="110" y2="105" stroke="currentColor" strokeWidth="2" opacity="0.3" />
            </svg>
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Your insights will appear here</h2>
            <p className="text-sm text-[var(--text-muted)] max-w-md mx-auto mb-6">
              After you've generated some content, this dashboard will show your usage patterns, content breakdown, and activity trends.
            </p>
            <Link to="/generate" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF4400] text-white rounded-md text-sm font-medium hover:bg-[#FF6633] transition-colors" data-testid="insights-start-generating">
              <Lightning size={16} weight="fill" />Start Generating
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="summary-cards">
              <StatCard label="Pieces Created" value={formatNum(s.total_generated)} trend={s.trend_pct} icon={LightningIcon} delay={0} />
              <StatCard label="Est. Words Generated" value={formatNum(s.total_words_estimated)} icon={Notebook} delay={0.05} />
              <StatCard label="Content Repurposed" value={formatNum(s.repurpose_count)} icon={ArrowsClockwise} delay={0.1} />
              <StatCard label="Days Active" value={s.days_active} icon={CalendarBlank} delay={0.15} />
            </div>

            <PlanThisMonthCTA />

            <DriftAnalysisSection
              driftData={driftData} driftLoading={driftLoading} driftTrend={driftTrend}
              correctingAssetId={correctingAssetId} onRefresh={fetchDrift} onCorrect={proposeCorrection}
            />

            <ContentTypeChart byContentType={data.by_content_type} />

            <div className="grid lg:grid-cols-2 gap-6">
              <WeeklyActivityChart byWeek={data.by_week} />
              <BusinessStatusChart byBusiness={data.by_business} statusBreakdown={data.status_breakdown} />
            </div>

            <TopTopicsSection topTopics={data.top_topics} />
            <AchievementsSection achievementsOpen={achievementsOpen} setAchievementsOpen={setAchievementsOpen} user={user} />
          </div>
        )}
      </main>
    </div>
  );
}
